import type { ReportCollection, ReportFormat } from "@/types/api";

import type { CreateExportInput } from "./use-collection-mutations";

/**
 * The formats a report can be exported as.
 *
 * Exactly the two the generator has renderers for
 * (`reports/models.py`: `choices=[('pdf', 'PDF'), ('pptx', 'PPTX')]`). Offering
 * a third would not produce a third kind of file — it would produce a failed
 * export several minutes later, which is worse than not offering it.
 */
export const EXPORT_FORMATS: readonly ReportFormat[] = ["pdf", "pptx"];

type ExportDefaults = Pick<ReportCollection, "format" | "orientation">;

/**
 * The format the next export will use.
 *
 * The collection's stored `format` is a **last-used pre-selection, not the
 * report's identity** — a report is a list of photos or issues, and the same
 * list is a deck for one audience and a PDF for another. So the picker opens on
 * what was used last, and choosing differently changes this export without
 * moving the collection's stored default.
 */
export function resolveExportFormat(
  collection: Pick<ReportCollection, "format">,
  override: ReportFormat | null,
): ReportFormat {
  return override ?? collection.format;
}

/**
 * The body for `POST .../collections/{cid}/exports/`.
 *
 * Every field is optional on the wire and falls back to what the collection
 * remembers ([[report-collections-api.spec]]), so this sends what the user's
 * choice actually decides and nothing else.
 *
 * `orientation` is **dropped for PPTX**, per the same contract: a deck has no
 * page orientation, and echoing back a portrait/landscape the collection is
 * still carrying from an earlier PDF export would put a field on the request
 * that means nothing to the renderer reading it.
 */
export function buildExportInput(
  collection: ExportDefaults,
  override: ReportFormat | null,
): CreateExportInput {
  const format = resolveExportFormat(collection, override);
  if (format === "pptx") return { format };
  return collection.orientation
    ? { format, orientation: collection.orientation }
    : { format };
}
